import { Routes, Route, Navigate, useParams } from 'react-router-dom';
import { useSelector } from 'react-redux';

import { crmBase } from '@crm/crmPaths';
import { hasAnyCrmPermission, hasCrmPermission } from '@crm/permissions';

import { AuthSessionSync } from '@/app/AuthSessionSync';
import { MainLayout } from '@main/layouts/MainLayout';
import { ProfileLayout } from '@main/layouts/ProfileLayout';
import { LoginPage } from '@main/pages/LoginPage';
import { ResetPasswordPage } from '@main/pages/ResetPasswordPage';
import { ProfilePage } from '@main/pages/ProfilePage';
import { BranchesPage } from '@main/pages/BranchesPage';
import { BranchFormPage } from '@main/pages/BranchFormPage';
import { SettingsPage } from '@main/pages/SettingsPage';
import { OwnerEventsPage } from '@main/pages/OwnerEventsPage';
import { OwnerEventFormPage } from '@main/pages/OwnerEventFormPage';
import { OwnerCommonExpensesPage } from '@main/pages/OwnerCommonExpensesPage';

import { CrmLayout } from '@crm/layouts/CrmLayout';
import { CrmLoginPage } from '@crm/pages/CrmLoginPage';
import { CrmResetPasswordPage } from '@crm/pages/CrmResetPasswordPage';
import { BranchPickerPage } from '@crm/pages/BranchPickerPage';
import { BranchLayout } from '@crm/layouts/BranchLayout';
import { DashboardPage } from '@crm/pages/DashboardPage';
import { ClientsPage } from '@crm/pages/ClientsPage';
import { ClientDetailPage } from '@crm/pages/ClientDetailPage';
import { CalendarPage } from '@crm/pages/CalendarPage';
import { SalePage } from '@crm/pages/SalePage';
import { DirectionDetailPage, DirectionsPage } from '@crm/pages/DirectionsPage';
import { StaffDetailPage, StaffsPage } from '@crm/pages/StaffsPage';
import { GoodsPage } from '@crm/pages/GoodsPage';
import { ExpendituresPage } from '@crm/pages/ExpendituresPage';
import { EventsPage } from '@crm/pages/EventsPage';
import { SalesFunnelPage } from '@crm/pages/SalesFunnelPage';
import { CrmSettingsPage } from '@crm/pages/CrmSettingsPage';

import { NotFoundPage } from '@shared/pages/NotFoundPage';
import NotificationPush from '@shared/common/notificationPush/NotificationPush';

function RequireOwner({ children }) {
  const { status, sessionChecked } = useSelector((s) => s.ownerAuth);
  if (!sessionChecked) {
    return (
      <div className="auth-card" style={{ margin: '40px auto' }}>
        Проверка сессии…
      </div>
    );
  }
  if (status === 'authenticated') return children;
  return <Navigate to="/" replace />;
}

function RequireManager({ children }) {
  const { ownerId } = useParams();
  const { status, sessionChecked } = useSelector((s) => s.crmAuth);
  if (!sessionChecked) {
    return (
      <div className="auth-card" style={{ margin: '40px auto' }}>
        Проверка сессии…
      </div>
    );
  }
  if (status === 'authenticated') return children;
  const loginTo = ownerId ? crmBase(ownerId) : '/';
  return <Navigate to={loginTo} replace />;
}

function RequireCrmPermission({ children, permission, anyOf }) {
  const { branchId } = useParams();
  const { profile, branches } = useSelector((s) => s.crmAuth);
  const allowed = permission
    ? hasCrmPermission(profile, branches, branchId, permission)
    : hasAnyCrmPermission(profile, branches, branchId, anyOf ?? []);

  if (allowed) return children;

  return (
    <div className="placeholder">
      Недостаточно прав для просмотра раздела.
    </div>
  );
}

export function AppRoutes() {
  return (
    <>
      <AuthSessionSync />
      <NotificationPush />
      <Routes>
      {/* --- Owner zone --- */}
      <Route element={<MainLayout />}>
        <Route index element={<LoginPage />} />
        <Route path="reset-password" element={<ResetPasswordPage />} />
      </Route>

      <Route
        path="/profile"
        element={
          <RequireOwner>
            <ProfileLayout />
          </RequireOwner>
        }
      >
        <Route index element={<ProfilePage />} />
        <Route path="branches" element={<BranchesPage />} />
        <Route path="branches/new" element={<BranchFormPage />} />
        <Route path="branches/:branchId" element={<BranchFormPage />} />
        <Route path="events" element={<OwnerEventsPage />} />
        <Route path="events/new" element={<OwnerEventFormPage />} />
        <Route path="events/:eventId" element={<OwnerEventFormPage />} />
        <Route path="expenses" element={<OwnerCommonExpensesPage />} />
        <Route path="settings" element={<SettingsPage />} />
      </Route>

      {/* Старые ссылки /crm — без id владельца */}
      <Route path="/crm" element={<Navigate to="/" replace />} />
      <Route path="/crm/*" element={<Navigate to="/" replace />} />

      {/* CRM: /{ownerId}/crm/... — ссылку можно передать админам */}
      <Route path="/:ownerId/crm" element={<CrmLayout />}>
        <Route index element={<CrmLoginPage />} />
        <Route path="reset-password" element={<CrmResetPasswordPage />} />
        <Route
          path="branches"
          element={
            <RequireManager>
              <BranchPickerPage />
            </RequireManager>
          }
        />
        <Route
          path=":branchId"
          element={
            <RequireManager>
              <BranchLayout />
            </RequireManager>
          }
        >
          <Route
            index
            element={
              <RequireCrmPermission permission="menu.dashboard">
                <DashboardPage />
              </RequireCrmPermission>
            }
          />
          <Route
            path="calendar"
            element={
              <RequireCrmPermission permission="menu.calendar">
                <CalendarPage />
              </RequireCrmPermission>
            }
          />
          <Route
            path="clients"
            element={
              <RequireCrmPermission permission="menu.clients">
                <ClientsPage />
              </RequireCrmPermission>
            }
          />
          <Route
            path="clients/new"
            element={
              <RequireCrmPermission permission="clients.create">
                <ClientDetailPage />
              </RequireCrmPermission>
            }
          />
          <Route
            path="clients/:clientId"
            element={
              <RequireCrmPermission permission="client.detail.view">
                <ClientDetailPage />
              </RequireCrmPermission>
            }
          />
          <Route
            path="sale"
            element={
              <RequireCrmPermission permission="menu.sale">
                <SalePage />
              </RequireCrmPermission>
            }
          />
          <Route
            path="directions"
            element={
              <RequireCrmPermission permission="menu.directions">
                <DirectionsPage />
              </RequireCrmPermission>
            }
          />
          <Route
            path="directions/:directionId"
            element={
              <RequireCrmPermission permission="direction.detail.view">
                <DirectionDetailPage />
              </RequireCrmPermission>
            }
          />
          <Route
            path="staffs"
            element={
              <RequireCrmPermission permission="menu.staffs">
                <StaffsPage />
              </RequireCrmPermission>
            }
          />
          <Route
            path="staffs/:managerId"
            element={
              <RequireCrmPermission permission="staff.detail.view">
                <StaffDetailPage />
              </RequireCrmPermission>
            }
          />
          <Route
            path="goods"
            element={
              <RequireCrmPermission permission="menu.goods">
                <GoodsPage />
              </RequireCrmPermission>
            }
          />
          <Route
            path="expenditures"
            element={
              <RequireCrmPermission permission="menu.expenditures">
                <ExpendituresPage />
              </RequireCrmPermission>
            }
          />
          <Route
            path="events"
            element={
              <RequireCrmPermission permission="menu.events">
                <EventsPage />
              </RequireCrmPermission>
            }
          />
          <Route
            path="funnel"
            element={
              <RequireCrmPermission permission="menu.funnel">
                <SalesFunnelPage />
              </RequireCrmPermission>
            }
          />
          <Route
            path="settings"
            element={
              <RequireCrmPermission permission="settings.view">
                <CrmSettingsPage />
              </RequireCrmPermission>
            }
          />
        </Route>
      </Route>

      <Route path="*" element={<NotFoundPage />} />
    </Routes>
    </>
  );
}
